import { useState, useEffect } from 'react'
import { apiFetch } from '../lib/api'

function Stars({ score, size = 'text-lg' }) {
  return (
    <span className={size}>
      {[1,2,3,4,5].map(n => (
        <span key={n} className={n <= score ? 'text-yellow-500' : 'text-gray-300'}>★</span>
      ))}
    </span>
  )
}

export default function UserProfileModal({ user, onClose, onChat }) {
  const [ratings, setRatings]             = useState([])
  const [loadingRatings, setLoadingRatings] = useState(true)

  useEffect(() => {
    setLoadingRatings(true)
    apiFetch(`/ratings/${user.id}`)
      .then(r => r.json())
      .then(data => setRatings(Array.isArray(data) ? data : []))
      .catch(() => setRatings([]))
      .finally(() => setLoadingRatings(false))
  }, [user.id])

  const avgScore = ratings.length
    ? (ratings.reduce((s, r) => s + r.score, 0) / ratings.length).toFixed(1)
    : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="glass-card w-full max-w-lg rounded-3xl border border-white/60 shadow-2xl fade-up overflow-y-auto max-h-[90vh]"
        onClick={e => e.stopPropagation()}>

        <div className="relative h-24 rounded-t-3xl bg-gradient-to-r from-orange-400 to-red-400">
          <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl leading-none">✕</button>
        </div>

        <div className="px-6 pb-6">
          <div className="-mt-10 flex items-end justify-between gap-4">
            <img
              src={user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(user.name)}`}
              alt={user.name}
              className="h-20 w-20 rounded-2xl object-cover bg-orange-100 border-4 border-white shadow-md shrink-0"
            />
            <div className="mb-1 text-right min-w-0">
              {avgScore ? (
                <div className="flex items-center justify-end gap-1.5">
                  <Stars score={Math.round(avgScore)} />
                  <span className="text-sm font-bold text-gray-800">{avgScore}</span>
                  <span className="text-xs text-gray-500">({ratings.length})</span>
                </div>
              ) : (
                <span className="text-xs text-gray-400">No ratings yet</span>
              )}
              <span className={`mt-1 inline-block rounded-full px-3 py-0.5 text-xs font-semibold ${user.availability?.isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {user.availability?.isAvailable ? '🟢 Available' : '⭕ Unavailable'}
              </span>
            </div>
          </div>

          <h2 className="title-font mt-3 text-2xl font-bold text-gray-900">{user.name}</h2>
          {user.bio && <p className="mt-1 text-sm text-gray-600 leading-relaxed">{user.bio}</p>}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-orange-50 border border-orange-100 px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-1">📍 Location</p>
              <p className="text-sm font-semibold text-gray-800">{user.location?.area}</p>
              <p className="text-xs text-gray-500">{user.location?.city}</p>
            </div>
            <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-1">🍽️ Preferences</p>
              <p className="text-sm font-semibold text-gray-800">{user.preferences?.dietaryType}</p>
              <p className="text-xs text-gray-500">{user.preferences?.budgetVibe}</p>
            </div>
          </div>

          {user.preferences?.interests?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Interests</p>
              <div className="flex flex-wrap gap-2">
                {user.preferences.interests.map(i => (
                  <span key={i} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">{i}</span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Ratings & Reviews</p>
              {avgScore && (
                <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-bold text-yellow-700">{avgScore} / 5</span>
              )}
            </div>

            {loadingRatings ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : ratings.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No reviews yet.</p>
            ) : (
              <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                {ratings.map(r => (
                  <div key={r.id} className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <img
                          src={r.giver?.profile?.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(r.giver?.name || 'user')}`}
                          className="h-7 w-7 rounded-lg object-cover bg-orange-100 shrink-0"
                          alt={r.giver?.name}
                        />
                        <p className="text-sm font-semibold text-gray-800 truncate">{r.giver?.name || 'Anonymous'}</p>
                      </div>
                      <Stars score={r.score} size="text-base" />
                    </div>
                    {r.comment && (
                      <p className="mt-2 text-sm text-gray-600 leading-relaxed">{r.comment}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">{new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {onChat && (
          <div className="px-6 pb-6 pt-2">
            <button
              onClick={onChat}
              className="w-full rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 transition"
            >
              💬 Start Chat
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
